import { useQuery } from '@apollo/client/react';
import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import {
    Plus, Trophy, Loader2, Ticket, X, Zap, Search, BadgeCheck,
} from 'lucide-react';
import { cn, formatScore } from '@/lib/utils';
import { PageContent } from '@/components/layout/PageContent';
import { Page } from '@/components/layout/Page';
import {
    LIST_RACES, RACES_TO_BET, RACES_MY_OPEN_BETS, RACES_MY_RESULTS,
} from '@/graphql/queries';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { HomeSkeleton } from '@/components/skeletons/HomeSkeleton';
import { SearchBar } from '@/components/ui/SearchBar';
import { RaceSection } from '@/components/races/RaceSection';
import { RaceCard } from '@/components/races/RaceCard';
import { RaceStatus, RaceSortField, SortOrder, type RaceFilterInput } from '@/gql/graphql';
import { isRaceLive, isRacing } from '@/lib/live';

// --- Constants ---

const LIST_PAGE_SIZE = 9;
const SECTION_TO_BET_LIMIT = 6;
const SECTION_SMALL_LIMIT = 3;

const DEADLINE_ASC = { field: RaceSortField.BettingDeadline, order: SortOrder.Asc };
const DEADLINE_DESC = { field: RaceSortField.BettingDeadline, order: SortOrder.Desc };

const STATUS_FILTERS = [null, RaceStatus.Open, RaceStatus.Ended, RaceStatus.Archived] as const;

// --- Main Page ---

export function RacesPage() {
    const { t } = useTranslation('races');
    const { t: tc } = useTranslation();

    // Filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<RaceStatus | null>(null);
    const [officialFilter, setOfficialFilter] = useState(false);
    const [forceListMode, setForceListMode] = useState(false);
    const [debouncedSearch] = useDebounce(searchTerm, 300);

    const showListMode = forceListMode || !!debouncedSearch || !!statusFilter || officialFilter;

    // --- Section queries (only when no filter active) ---

    const { data: toBetData, loading: toBetLoading } = useQuery(RACES_TO_BET, {
        variables: { pagination: { page: 1, limit: SECTION_TO_BET_LIMIT }, sort: DEADLINE_ASC },
        skip: showListMode,
    });

    const { data: myOpenBetsData, loading: myOpenBetsLoading } = useQuery(RACES_MY_OPEN_BETS, {
        variables: { pagination: { page: 1, limit: SECTION_SMALL_LIMIT }, sort: DEADLINE_ASC },
        skip: showListMode,
    });

    const { data: myResultsData, loading: myResultsLoading } = useQuery(RACES_MY_RESULTS, {
        variables: { pagination: { page: 1, limit: SECTION_SMALL_LIMIT }, sort: DEADLINE_DESC },
        skip: showListMode,
    });

    const sectionsLoading = toBetLoading || myOpenBetsLoading || myResultsLoading;

    // --- List query (only when filter active) ---

    const listFilter = useMemo<RaceFilterInput | undefined>(() => {
        const f: RaceFilterInput = {};
        if (debouncedSearch) f.search = debouncedSearch;
        if (statusFilter) f.status = statusFilter;
        if (officialFilter) f.isOfficial = true;
        return Object.keys(f).length > 0 ? f : undefined;
    }, [debouncedSearch, statusFilter, officialFilter]);

    const { data: listData, previousData: listPrev, loading: listLoading, error: listError, fetchMore } = useQuery(LIST_RACES, {
        variables: { pagination: { page: 1, limit: LIST_PAGE_SIZE }, filter: listFilter },
        skip: !showListMode,
        notifyOnNetworkStatusChange: true,
    });

    const listDisplay = listData ?? listPrev;

    // --- Handlers ---

    const clearFilters = useCallback(() => {
        setSearchTerm('');
        setStatusFilter(null);
        setOfficialFilter(false);
        setForceListMode(false);
    }, []);

    const activateFilter = useCallback((status: RaceStatus | null, search?: string) => {
        if (status) setStatusFilter(status);
        if (search !== undefined) setSearchTerm(search);
        setForceListMode(true);
    }, []);

    const handleLoadMore = async () => {
        const currentPage = listData?.listRaces?.page || 1;
        await fetchMore({
            variables: {
                pagination: { page: currentPage + 1, limit: LIST_PAGE_SIZE },
                filter: listFilter,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prev;
                return {
                    listRaces: {
                        ...fetchMoreResult.listRaces,
                        items: [
                            ...(prev.listRaces?.items || []),
                            ...(fetchMoreResult.listRaces?.items || []),
                        ],
                    },
                };
            },
        });
    };

    const getStatusLabel = (status: RaceStatus | null) => {
        if (status === null) return t('dashboard.filter.all');
        if (status === RaceStatus.Open) return tc('status.open');
        if (status === RaceStatus.Ended) return t('dashboard.filter.ended');
        return t('dashboard.filter.archived');
    };

    // --- Loading / Error states ---

    if (!showListMode && sectionsLoading && !toBetData && !myOpenBetsData && !myResultsData) {
        return <HomeSkeleton />;
    }

    if (listError) {
        return <div className="p-4"><ErrorBanner message={tc('error.generic')} /></div>;
    }

    // --- Extract section data ---

    const toBetRaces = (toBetData?.listRaces?.items || []).filter((r): r is typeof r & { id: string } => !!r.id);
    const toBetTotal = toBetData?.listRaces?.totalCount || 0;

    const myOpenBets = (myOpenBetsData?.listRaces?.items || []).filter((r): r is typeof r & { id: string } => !!r.id);
    const myOpenBetsTotal = myOpenBetsData?.listRaces?.totalCount || 0;

    const myResults = (myResultsData?.listRaces?.items || []).filter((r): r is typeof r & { id: string } => !!r.id);
    const myResultsTotal = myResultsData?.listRaces?.totalCount || 0;

    const listRaces = (listDisplay?.listRaces?.items || []).filter((r): r is typeof r & { id: string } => !!r.id);
    const listHasNextPage = listDisplay?.listRaces?.hasNextPage;

    return (
        <Page className="bg-slate-50">
            <PageContent width="wide" className="space-y-6 pb-24 md:pb-6">
                {/* --- HEADER --- */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-black text-slate-900">{t('dashboard.title')}</h1>
                        <p className="text-slate-500 text-sm">{t('dashboard.subtitle')}</p>
                    </div>
                    <Link
                        to="/races/create"
                        className="bg-emerald-600 text-white px-5 py-3 rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-200 active:scale-95"
                    >
                        <Plus size={20} />
                        {tc('nav.createRace')}
                    </Link>
                </div>

                {/* --- SEARCH & FILTERS --- */}
                <div className="space-y-3">
                    <SearchBar
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onClear={() => setSearchTerm('')}
                        isLoading={listLoading && !!debouncedSearch}
                        placeholder={t('dashboard.search.placeholder')}
                    />
                    <div className="flex gap-2 flex-wrap">
                        {STATUS_FILTERS.map((status) => (
                            <button
                                key={status ?? 'all'}
                                onClick={() => setStatusFilter(status)}
                                className={cn(
                                    "px-3 py-1.5 rounded-xl text-xs font-bold transition-all",
                                    statusFilter === status
                                        ? "bg-emerald-600 text-white shadow-sm"
                                        : "bg-slate-100 text-slate-500 hover:bg-slate-200",
                                )}
                            >
                                {getStatusLabel(status)}
                            </button>
                        ))}
                        <button
                            onClick={() => setOfficialFilter(!officialFilter)}
                            className={cn(
                                "px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1",
                                officialFilter
                                    ? "bg-amber-500 text-white shadow-sm"
                                    : "bg-slate-100 text-slate-500 hover:bg-slate-200",
                            )}
                        >
                            <BadgeCheck size={12} />
                            {t('dashboard.filter.official')}
                        </button>
                    </div>
                </div>

                {/* --- MODE: SECTIONS (default) --- */}
                {!showListMode && (
                    <div className="space-y-8">
                        {/* Section: À parier */}
                        <RaceSection
                            title={t('dashboard.sections.toBet.title')}
                            icon={<Zap size={18} className="text-emerald-500" />}
                            isEmpty={toBetRaces.length === 0}
                            emptyMessage={t('dashboard.sections.toBet.empty')}
                            totalCount={toBetTotal}
                            displayedCount={toBetRaces.length}
                            onSeeAll={() => activateFilter(RaceStatus.Open)}
                            seeAllLabel={t('dashboard.sections.toBet.seeAll', { count: toBetTotal })}
                            accentBorder="border-l-emerald-400"
                        >
                            {toBetRaces.map((race) => (
                                <RaceCard
                                    key={race.id}
                                    id={race.id}
                                    name={race.name ?? ''}
                                    raceFormat={race.raceFormat}
                                    bettingDeadline={race.bettingDeadline}
                                    runnerCount={race.runnerCount ?? 0}
                                    bettorCount={race.bettorCount ?? 0}
                                    isOfficial={race.isOfficial}
                                    isLive={isRaceLive(race)}
                                    accentClass="border-emerald-100 bg-emerald-50/30 hover:border-emerald-200"
                                />
                            ))}
                        </RaceSection>

                        {/* Section: Mes paris en cours */}
                        <RaceSection
                            title={t('dashboard.sections.myOpenBets.title')}
                            icon={<Ticket size={18} className="text-amber-500" />}
                            isEmpty={myOpenBets.length === 0}
                            emptyMessage={t('dashboard.sections.myOpenBets.empty')}
                            totalCount={myOpenBetsTotal}
                            displayedCount={myOpenBets.length}
                            onSeeAll={() => activateFilter(RaceStatus.Open)}
                            seeAllLabel={t('dashboard.sections.myOpenBets.seeAll', { count: myOpenBetsTotal })}
                            accentBorder="border-l-amber-400"
                        >
                            {myOpenBets.map((race) => (
                                <RaceCard
                                    key={race.id}
                                    id={race.id}
                                    name={race.name ?? ''}
                                    raceFormat={race.raceFormat}
                                    bettingDeadline={race.bettingDeadline}
                                    runnerCount={race.runnerCount ?? 0}
                                    bettorCount={race.bettorCount ?? 0}
                                    isOfficial={race.isOfficial}
                                    isLive={isRaceLive(race)}
                                    badges={
                                        <div className="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-50 text-amber-700 flex items-center gap-1">
                                            <Ticket size={10} />
                                            {t('dashboard.betPlaced')}
                                        </div>
                                    }
                                />
                            ))}
                        </RaceSection>

                        {/* Section: Mes résultats */}
                        <RaceSection
                            title={t('dashboard.sections.myResults.title')}
                            icon={<Trophy size={18} className="text-amber-500" />}
                            isEmpty={myResults.length === 0}
                            emptyMessage={t('dashboard.sections.myResults.empty')}
                            totalCount={myResultsTotal}
                            displayedCount={myResults.length}
                            onSeeAll={() => activateFilter(RaceStatus.Ended)}
                            seeAllLabel={t('dashboard.sections.myResults.seeAll', { count: myResultsTotal })}
                            accentBorder="border-l-slate-300"
                        >
                            {myResults.map((race) => (
                                <RaceCard
                                    key={race.id}
                                    id={race.id}
                                    name={race.name ?? ''}
                                    raceFormat={race.raceFormat}
                                    bettingDeadline={race.bettingDeadline}
                                    runnerCount={race.runnerCount ?? 0}
                                    bettorCount={race.bettorCount ?? 0}
                                    isOfficial={race.isOfficial}
                                    badges={
                                        race.currentUserScore != null ? (
                                            <div className="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700">
                                                {formatScore(race.currentUserScore)} pts
                                            </div>
                                        ) : undefined
                                    }
                                />
                            ))}
                        </RaceSection>

                        {/* CTA: Explorer toutes les courses */}
                        <div className="flex justify-center pt-2">
                            <button
                                onClick={() => setForceListMode(true)}
                                className="bg-white border border-slate-200 px-5 py-2.5 rounded-xl font-bold text-sm text-slate-600 hover:text-emerald-600 hover:border-emerald-200 flex items-center gap-2 transition-colors shadow-sm"
                            >
                                <Search size={14} />
                                {t('dashboard.exploreAll')}
                            </button>
                        </div>
                    </div>
                )}

                {/* --- MODE: FLAT LIST (filter active) --- */}
                {showListMode && (
                    <div className="space-y-4">
                        {/* Back to overview */}
                        <button
                            onClick={clearFilters}
                            className="text-sm font-semibold text-emerald-600 hover:text-emerald-700 flex items-center gap-1 transition-colors"
                        >
                            <X size={14} />
                            {t('dashboard.backToOverview')}
                        </button>

                        {/* Grid */}
                        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                            {listRaces.map((race) => {
                                const live = isRaceLive(race);
                                const racing = isRacing(race);

                                const statusLabel = racing ? tc('status.racing')
                                    : race.status === RaceStatus.Open ? tc('status.open')
                                        : race.status === RaceStatus.Ended ? tc('status.ended')
                                            : tc('status.archived');
                                const statusColor = racing ? "bg-rose-50 text-rose-700"
                                    : race.status === RaceStatus.Open ? "bg-emerald-50 text-emerald-700"
                                        : race.status === RaceStatus.Ended ? "bg-amber-50 text-amber-700"
                                            : "bg-slate-100 text-slate-500";

                                const statusBadge = (
                                    <div className={cn(
                                        "shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide",
                                        statusColor,
                                    )}>
                                        {statusLabel}
                                    </div>
                                );

                                const badges = (
                                    <div className="flex items-center gap-1 shrink-0">
                                        {race.hasCurrentUserBet && race.currentUserScore != null && (
                                            <div className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700">
                                                {formatScore(race.currentUserScore)} pts
                                            </div>
                                        )}
                                        {race.hasCurrentUserBet && race.currentUserScore == null && (
                                            <div className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-50 text-amber-700 flex items-center gap-1">
                                                <Ticket size={10} />
                                                {t('dashboard.betPlaced')}
                                            </div>
                                        )}
                                        {statusBadge}
                                    </div>
                                );

                                return (
                                    <RaceCard
                                        key={race.id}
                                        id={race.id}
                                        name={race.name ?? ''}
                                        raceFormat={race.raceFormat}
                                        bettingDeadline={race.bettingDeadline}
                                        runnerCount={race.runnerCount ?? 0}
                                        bettorCount={race.bettorCount ?? 0}
                                        isOfficial={race.isOfficial}
                                        isLive={live}
                                        badges={badges}
                                    />
                                );
                            })}
                        </div>

                        {/* Empty state */}
                        {listRaces.length === 0 && !listLoading && (
                            <div className="py-12 text-center bg-white rounded-2xl border border-dashed border-slate-200">
                                <p className="text-slate-400 font-medium">{t('dashboard.noResults')}</p>
                                <button
                                    onClick={clearFilters}
                                    className="text-emerald-600 font-bold text-sm mt-2 inline-flex items-center gap-1 hover:underline"
                                >
                                    <X size={14} />
                                    {t('dashboard.clearFilters')}
                                </button>
                            </div>
                        )}

                        {/* Load more */}
                        {listHasNextPage && (
                            <div className="pt-4 flex justify-center">
                                <button
                                    onClick={handleLoadMore}
                                    disabled={listLoading}
                                    className="bg-white border border-slate-200 text-slate-600 px-6 py-3 rounded-xl font-bold text-sm hover:bg-slate-50 hover:text-slate-900 transition-colors flex items-center gap-2 shadow-sm"
                                >
                                    {listLoading ? (
                                        <>
                                            <Loader2 className="animate-spin" size={16} />
                                            {tc('btn.loading')}
                                        </>
                                    ) : (
                                        t('dashboard.loadMore')
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </PageContent>
        </Page>
    );
}
